import { createStyles, FormHelperText, makeStyles } from "@material-ui/core";
import { Field } from "formik";
import * as React from "react";

import { contactPrefs, timesOfDay } from ".";
import {
    ChipCollection,
    ChipCollectionBestTimes,
    domain,
    IChip,
    RelodeTheme,
} from "../../";

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            chipSection: {
                width: "auto",
                marginTop: theme.spacing(2),
                [theme.breakpoints.down("md")]: {},
            },
            section: {
                marginBottom: theme.spacing(3),
            },
            helperText: {
                color: theme.palette.error.main,
                marginBottom: theme.spacing(3),
            },
        }),
    { name: "BestReachOptions" }
);

interface Props {
    baseId: string;
    hasBestWay?: boolean;
    hasBestDays?: boolean;
    hasBestTimes?: boolean;
    hideSelectAll?: boolean;
    isReviewWizard?: boolean;
    error?: boolean;
    helperText?: string;
    selectedTags: domain.TagMatchType[];
    onChange?: (selectedTags: domain.TagMatchType[]) => void;
}

export const BestReachOptions = (props: Props) => {
    const classes = useStyles({});
    const {
        baseId,
        hasBestWay,
        hasBestTimes,
        hideSelectAll,
        isReviewWizard = false,
        error,
        helperText,
        selectedTags = [],
        onChange = (...args) => {
            /* noop */
        },
    } = props;
    const allIds: domain.TagMatchType[] = [
        ...contactPrefs.contactMethods,
        ...timesOfDay,
    ];

    const isContactPreferenceSelected = (currentPreferenceId: number) =>
        !!selectedTags.find(i => i.tagId === currentPreferenceId);

    const contactMethodChips = convertTagsToChips(
        [...contactPrefs.contactMethods],
        isContactPreferenceSelected
    );

    const contactDayTimeChips = (() =>
        contactPrefs.timeOfDay.map(day => ({
            day: day.day,
            times: day.times.map(time => {
                time.isMatched = !!selectedTags.find(
                    tag => tag.tagId === time.tagId
                );
                return time;
            }),
        })))();

    const handleSelectAll = (checked: boolean) => {
        const selectedMethods = contactPrefs.contactMethods.filter(t =>
            selectedTags.some(s => s.tagId === t.tagId)
        );
        onChange(
            checked ? [...selectedMethods, ...timesOfDay] : selectedMethods
        );
    };

    const handleToggle = (tagId: number) => {
        let selected = [...selectedTags];
        const tag = selected.find(t => t.tagId === tagId);

        if (!tag) {
            const newTag = allIds.find(t => t.tagId === tagId);
            if (newTag) {
                selected.push(newTag);
            }
        } else {
            selected = selected.filter(t => t.tagId !== tagId);
        }

        onChange(selected);
    };

    return (
        <>
            {hasBestWay && (
                <div className={classes.chipSection}>
                    <Field
                        component={ChipCollection}
                        style={{ width: 100 }}
                        description="best way to reach"
                        chips={contactMethodChips}
                        onChipClick={handleToggle}
                        id={baseId}
                        dataCy="select-best-way-to-reach-"
                    />
                </div>
            )}
            {hasBestTimes && (
                <div className={classes.chipSection}>
                    <Field
                        component={ChipCollectionBestTimes}
                        style={{ width: 100, fontSize: 16 }}
                        description="best times to reach"
                        onChipClick={handleToggle}
                        onSelectAll={handleSelectAll}
                        daysAndTimes={contactDayTimeChips}
                        hideSelectAll={hideSelectAll}
                        baseId={baseId}
                        dataCy="select-best-times-to-reach-"
                        isReviewWizard={isReviewWizard}
                    />
                </div>
            )}
            {error ? (
                <FormHelperText className={classes.helperText}>
                    {helperText}
                </FormHelperText>
            ) : null}
        </>
    );
};

export const convertTagsToChips = (
    tags?: domain.TagMatchType[],
    checker?: (id: number) => boolean | undefined
): IChip[] => {
    const chips: IChip[] = [];
    tags &&
        tags.map(tag => {
            const { tagId, tagName, tagTypeId, canEdit, canMatch } = tag;
            const isMatched = checker ? checker(tagId) : true;
            chips.push({
                id: tagId,
                name: tagName,
                groupId: tagTypeId,
                isMatched,
                canEdit,
                canMatch,
            });
        });
    return chips;
};
